import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import careparvaReportService from '../../services/careparvaReportService';
import { showToast } from '../../components/toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSyncAlt,
  faHistory,
  faLayerGroup,
  faChartLine,
  faClock,
  faCheckCircle,
  faExclamationTriangle,
  faSpinner,
  faFileExcel,
} from '@fortawesome/free-solid-svg-icons';
import './CareparvaReport.css';

const CareparvaEntryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [entry, setEntry] = useState(location.state?.entry || null);
  const [loading, setLoading] = useState(!location.state?.entry);
  const [statusHistory, setStatusHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Load entry data & audit history
  const loadData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      let currentEntry = location.state?.entry;
      if (!currentEntry) {
        currentEntry = await careparvaReportService.getEntryById(id);
      }
      setEntry(currentEntry);

      if (currentEntry?.unique_id) {
        setLoadingHistory(true);
        try {
          const history = await careparvaReportService.getStatusHistory(currentEntry.unique_id);
          setStatusHistory(history || []);
        } catch (hErr) {
          console.error('Failed to load status history:', hErr);
        } finally {
          setLoadingHistory(false);
        }
      }
    } catch (err) {
      console.error('Failed to load entry details:', err);
      showToast.error('Could not load entry details.');
    } finally {
      setLoading(false);
    }
  }, [id, location.state]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Helper Badge Renderers
  const renderPtypeBadge = (ptype) => {
    switch (Number(ptype)) {
      case 1:
        return <span className="badge-clean badge-clean-intraday"><FontAwesomeIcon icon={faClock} className="me-1" /> Intraday Details</span>;
      case 2:
        return <span className="badge-clean badge-clean-stock"><FontAwesomeIcon icon={faChartLine} className="me-1" /> Single Stock Details</span>;
      case 3:
        return <span className="badge-clean badge-clean-derivative"><FontAwesomeIcon icon={faLayerGroup} className="me-1" /> Derivative Details</span>;
      default:
        return <span className="badge-clean">-</span>;
    }
  };

  const renderStatusBadge = (status) => {
    const s = String(status || '').toUpperCase();
    if (s === 'ACTIVE') return <span className="badge-clean badge-clean-buy">ACTIVE</span>;
    if (s === 'CLOSED') return <span className="badge-clean badge-clean-closed">CLOSED</span>;
    if (s === 'INVALID') return <span className="badge-clean badge-clean-invalid">INVALID</span>;
    if (s === 'REJECTED') return <span className="badge-clean badge-clean-rejected">REJECTED</span>;
    return <span className="badge-clean">{status || '-'}</span>;
  };

  const renderSystemStatus = (status) => {
    const num = Number(status);
    switch (num) {
      case 1:
        return <span className="badge-clean badge-clean-buy">Active</span>;
      case 2:
        return <span className="badge-clean badge-clean-intraday">Target 1</span>;
      case 3:
        return <span className="badge-clean badge-clean-intraday">Target 2</span>;
      case 4:
        return <span className="badge-clean badge-clean-sell">Stoploss</span>;
      case 5:
        return <span className="badge-clean badge-clean-closed">Closed</span>;
      default:
        return status !== null && status !== undefined && status !== '' ? (
          <span className="badge-clean">{status}</span>
        ) : (
          '-'
        );
    }
  };

  if (loading) {
    return (
      <div className="careparva-container mt-5 pt-2">
        <div className="bg-white p-5 rounded border text-center shadow-sm">
          <FontAwesomeIcon icon={faSpinner} spin className="me-2 text-primary" size="2x" />
          <h5 className="mt-3 text-muted">Loading entry details #{id}...</h5>
        </div>
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="careparva-container mt-5 pt-2">
        <div className="bg-white p-5 rounded border text-center shadow-sm">
          <FontAwesomeIcon icon={faFileExcel} className="me-2 text-danger" size="2x" />
          <h4 className="mt-3 text-dark fw-bold">Careparva Entry Not Found</h4>
          <p className="text-muted">The requested entry #{id} does not exist or has been removed.</p>
          <Link to="/research/careparva-report" className="btn btn-primary btn-sm mt-2">
            <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Back to Careparva Report
          </Link>
        </div>
      </div>
    );
  }

  // Parameter Comparison Data Source
  const comparisonRows = [
    {
      label: 'Service Name',
      systemVal: entry.service_name || '-',
      careparvaVal: entry.service_name || '-',
      highlight: true,
    },
    {
      label: 'Symbol',
      systemVal: entry.symbol_name || '-',
      careparvaVal: entry.symbol_name || '-',
      highlight: true,
    },
    {
      label: 'Advice ID',
      systemVal: entry.unique_id || '-',
      careparvaVal: entry.unique_id || '-',
      highlight: true,
    },
    {
      label: 'Sheet Type',
      systemVal: '-',
      careparvaVal: renderPtypeBadge(entry.ptype),
      highlight: null,
    },
    {
      label: 'Action / Recommendation',
      systemVal: entry.action === 1 ? 'BUY' : entry.action === 0 ? 'SELL' : '-',
      careparvaVal: entry.p_action || '-',
      highlight: entry.p_action && (
        (entry.action === 1 && String(entry.p_action).toUpperCase().includes('BUY')) ||
        (entry.action === 0 && String(entry.p_action).toUpperCase().includes('SELL'))
      ),
    },
    {
      label: 'Quantity',
      systemVal: entry.qty ?? '-',
      careparvaVal: '-',
      highlight: null,
    },
    {
      label: 'Entry Price (EP)',
      systemVal: entry.ep ?? '-',
      careparvaVal: entry.p_entry ?? '-',
      highlight: entry.ep && entry.p_entry && Number(entry.ep) === Number(entry.p_entry),
    },
    {
      label: 'EP Start / End Range',
      systemVal: '-',
      careparvaVal: entry.p_ep_start && entry.p_ep_end
        ? `${entry.p_ep_start} - ${entry.p_ep_end}`
        : entry.p_ep_start ?? entry.p_ep_end ?? '-',
      highlight: null,
    },
    {
      label: 'Target 1 / Target Price',
      systemVal: entry.tgt1 ?? '-',
      careparvaVal: entry.p_tgt ?? '-',
      highlight: entry.tgt1 && entry.p_tgt && Number(entry.tgt1) === Number(entry.p_tgt),
    },
    {
      label: 'Target 2',
      systemVal: entry.tgt2 ?? '-',
      careparvaVal: '-',
      highlight: null,
    },
    {
      label: 'Stop Loss (SL)',
      systemVal: entry.sl ?? '-',
      careparvaVal: entry.p_sl ?? '-',
      highlight: entry.sl && entry.p_sl && Number(entry.sl) === Number(entry.p_sl),
    },
    {
      label: 'Exit Price',
      systemVal: '-',
      careparvaVal: entry.p_exit ?? '-',
      highlight: null,
    },
    {
      label: 'Status',
      systemVal: renderSystemStatus(entry.status),
      careparvaVal: renderStatusBadge(entry.p_status),
      highlight: null,
    },
    {
      label: 'Reason / Outcome',
      systemVal: '-',
      careparvaVal: entry.p_reason || '-',
      highlight: null,
    },
    {
      label: 'Recommendation Horizon',
      systemVal: '-',
      careparvaVal: entry.p_horizon || '-',
      highlight: null,
    },
    {
      label: 'Is Intraday',
      systemVal: '-',
      careparvaVal: entry.p_intraday || '-',
      highlight: null,
    },
    {
      label: 'Derivative Strike Price',
      systemVal: '-',
      careparvaVal: entry.p_sp || '-',
      highlight: null,
    },
    {
      label: 'Derivative Expiry',
      systemVal: '-',
      careparvaVal: entry.p_d_exp || '-',
      highlight: null,
    },
    {
      label: 'Security Entry Date',
      systemVal: '-',
      careparvaVal: entry.s_entry_date || '-',
      highlight: null,
    },
    {
      label: 'Created Timestamp',
      systemVal: entry.n_created_on ? new Date(entry.n_created_on).toLocaleString() : '-',
      careparvaVal: entry.p_created_on || '-',
      highlight: null,
    },
  ];

  return (
    <div className="careparva-container mt-5 pt-2">
      {/* Page Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <Link to="/research/careparva-report" className="btn btn-default btn-sm">
            <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Back
          </Link>
          <h4 className="fw-bold mb-0 text-dark" style={{ fontSize: '18px' }}>
            Careparva Entry Details
          </h4>
        </div>
        <button
          type="button"
          className="btn btn-default btn-sm"
          onClick={loadData}
          title="Refresh Details"
        >
          <FontAwesomeIcon icon={faSyncAlt} className="me-1" /> Refresh
        </button>
      </div>

      {/* Parameter Comparison Table (System Call vs Careparva Call) */}
      <div className="bg-white rounded border shadow-sm mb-3">
        <div className="p-3 border-bottom bg-light d-flex align-items-center justify-content-between">
          <strong className="text-dark" style={{ fontSize: '14px' }}>
            <FontAwesomeIcon icon={faChartLine} className="me-2 text-success" /> Parameter Comparison: System Call vs Careparva Call
          </strong>
          <span className="small text-muted">Comparing live CRM call parameters against imported Careparva audit data</span>
        </div>
        <div className="table-responsive">
          <table className="table table-bordered table-hover mb-0 careparva-table">
            <thead>
              <tr>
                <th style={{ width: '25%' }}>Parameter / Field</th>
                <th style={{ width: '35%' }}>System Call (Internal)</th>
                <th style={{ width: '35%' }}>Careparva Call (Excel Report)</th>
                <th style={{ width: '5%', textAlign: 'center' }}>Match</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, i) => (
                <tr key={i}>
                  <td className="fw-semibold text-muted bg-light" style={{ fontSize: '12px' }}>
                    {row.label}
                  </td>
                  <td style={{ fontSize: '13px' }} className="fw-medium">
                    {row.systemVal}
                  </td>
                  <td style={{ fontSize: '13px' }} className="fw-medium">
                    {row.careparvaVal}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {row.highlight === true ? (
                      <FontAwesomeIcon icon={faCheckCircle} className="text-success" title="Matched" />
                    ) : row.highlight === false ? (
                      <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning" title="Difference" />
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Table 3: Status Audit History Table */}
      <div className="bg-white rounded border shadow-sm mb-4">
        <div className="p-3 border-bottom bg-light d-flex align-items-center justify-content-between">
          <strong className="text-dark" style={{ fontSize: '14px' }}>
            <FontAwesomeIcon icon={faHistory} className="me-2 text-primary" /> Status Audit History (Transitions)
          </strong>
          <span className="badge bg-secondary text-white">
            {statusHistory.length} Record{statusHistory.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="table-responsive">
          {loadingHistory ? (
            <div className="text-center py-4 text-muted">
              <FontAwesomeIcon icon={faSpinner} spin className="me-2 text-primary" />
              Loading status transition history...
            </div>
          ) : statusHistory.length === 0 ? (
            <div className="text-center py-4 text-muted">
              No previous status transitions recorded for Advice ID <strong>{entry.unique_id}</strong>.
            </div>
          ) : (
            <table className="table table-bordered table-hover mb-0 careparva-table">
              <thead>
                <tr>
                  <th style={{ width: '50px', textAlign: 'center' }}>#</th>
                  <th style={{ width: '150px' }}>Status</th>
                  <th>Reason / Exit Note</th>
                  <th style={{ width: '220px' }}>Recorded Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {statusHistory.map((h, i) => (
                  <tr key={h.id || i}>
                    <td style={{ textAlign: 'center', color: '#868e96' }}>{i + 1}</td>
                    <td>{renderStatusBadge(h.status)}</td>
                    <td className="fw-medium text-dark">{h.reason || '-'}</td>
                    <td className="small text-muted">
                      {h.created_on ? new Date(h.created_on).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default CareparvaEntryDetail;

